import React, { useCallback, useEffect, useRef } from 'react';
import classnames from 'classnames';

/**
 * RangeSlider — dual-handle range slider with overlaid <input type="range"> controls.
 *
 * Props:
 *   min        - minimum value (number)
 *   max        - maximum value (number)
 *   startVal   - current start value
 *   endVal     - current end value
 *   onStartChange(value) - called when start handle moves
 *   onEndChange(value)   - called when end handle moves
 *   step       - input step (default: 0.001)
 *   style      - additional styles for the container (e.g. background sprite)
 *   containerRef - optional ref for the container element
 */
const RangeSlider = ({
  min,
  max,
  startVal,
  endVal,
  onStartChange,
  onEndChange,
  step = 0.001,
  style,
  containerRef: externalContainerRef,
}) => {
  const startValRef = useRef(null);
  const endValRef = useRef(null);
  const rangeRef = useRef(null);

  const getPercent = useCallback(
    (value) => {
      if (max === min) return 0;
      return parseFloat(((value - min) / (max - min)) * 100).toFixed(3);
    },
    [min, max],
  );

  // Update range highlight from left
  useEffect(() => {
    if (endValRef.current && rangeRef.current) {
      const startPercent = getPercent(startVal);
      const endPercent = getPercent(+endValRef.current.value);
      rangeRef.current.style.left = `${startPercent}%`;
      rangeRef.current.style.width = `${endPercent - startPercent}%`;
    }
  }, [startVal, getPercent, endVal]);

  // Update range highlight from right
  useEffect(() => {
    if (startValRef.current && rangeRef.current) {
      const startPercent = getPercent(+startValRef.current.value);
      const endPercent = getPercent(endVal);
      rangeRef.current.style.width = `${endPercent - startPercent}%`;
    }
  }, [endVal, getPercent, startVal]);

  return (
    <div
      ref={externalContainerRef}
      className="range-slider-container"
      style={style}
    >
      <input
        name="field-slider-min"
        type="range"
        min={min}
        max={max}
        value={startVal}
        step={step}
        ref={startValRef}
        onChange={(event) => {
          const value = Math.min(+event.target.value, endVal - 1);
          onStartChange(value);
          event.target.value = value.toString();
        }}
        className={classnames('thumb thumb-min', {
          'thumb-zindex-5': startVal > max - 100,
        })}
      />
      <input
        name="field-slider-max"
        type="range"
        min={min}
        max={max}
        value={endVal}
        step={step}
        ref={endValRef}
        onChange={(event) => {
          const value = Math.max(+event.target.value, startVal + 1);
          onEndChange(value);
          event.target.value = value.toString();
        }}
        className="thumb thumb-max"
      />
      <div className="range-slider">
        <div className="range-slider-track" />
        <div ref={rangeRef} className="range-slider-range" />
      </div>
    </div>
  );
};

export default RangeSlider;
